import React, { useMemo } from 'react'
import { useLocation } from 'react-router-dom'

import styles from './Notifications.module.sass'
import { useGlobal } from '@jimmyboys/liita-ui/dist/components/GlobalContext'
import useQueryParams from '@jimmyboys/liita-ui/dist/hooks/useQueryParams'
import HStack from '@jimmyboys/liita-ui/dist/components/HStack'
import Dropdown from '@jimmyboys/liita-ui/dist/components/Dropdown'
import { useFetchList } from 'store'
import { Options, TrustNotification } from 'models'

export const sortOptionsFunc = (i18n) => [
	{
		value: 'date,asc',
		text: i18n`Date, asc.`,
	},
	{
		value: 'date,desc',
		text: i18n`Date, desc.`,
	},
]

/**
 * Optionally pass the current notifications results so the "Notification Type" dropdown
 * only shows the types that exist in the current result set.
 *
 * NOTE: If your notifications list is paginated, this will reflect the current page.
 * For "all results" behaviour you need API facets/aggregations.
 */
const NotificationsFilters = ({ notifications } = {}) => {
	const { i18n } = useGlobal()
	const location = useLocation()
	const [queryParams, , setQueryParams] = useQueryParams(location)

	const {
		contents: [
			insuranceCompanies,
			serviceProviders,
			bodyParts,
			clinicalStates,
			processInjuredStates,
			insuranceBranches,
			notificationTypes,
		],
	} = useFetchList(
		[
			'/metadata/insurance_coimpanies',
			'/metadata/provider_entities',
			'/metadata/body_parts',
			'/metadata/clinical_states',
			'/metadata/injured_states',
			'/metadata/insurance_branches',
			'/metadata/notification_types',
		],
		[
			Options.fromSimpleObjectJSON,
			Options.fromSimpleObjectJSON,
			Options.fromSimpleObjectJSON,
			Options.fromSimpleObjectJSON,
			Options.fromSimpleObjectJSON,
			Options.fromSimpleObjectJSON,
			(json) => {
				return {
					value: json.id.toString(),
					text: json.type,
				}
			},
		],
		60000 * 60 // One hour
	)

	const insuranceCompaniesFilters = Array.isArray(insuranceCompanies)
		? [
				{
					value: '',
					text: i18n`Any`,
				},
				...insuranceCompanies,
		  ]
		: []

	const selectedCompany = queryParams.company
		? insuranceCompaniesFilters.find(
				({ value }) =>
					queryParams.company.toLocaleLowerCase() ===
					value.toLocaleLowerCase()
		  )
		: null

	const providerFilters = Array.isArray(serviceProviders)
		? [
				{
					value: '',
					text: i18n`Any`,
				},
				...serviceProviders,
		  ]
		: []

	const selectedProvider = queryParams.providerEntity
		? providerFilters.find(
				({ value }) =>
					queryParams.providerEntity.toLocaleLowerCase() ===
					value.toLocaleLowerCase()
		  )
		: null

	const bodyPartFilters = Array.isArray(bodyParts)
		? [
				{
					value: '',
					text: i18n`Any`,
				},
				...bodyParts,
		  ]
		: []

	const selectedBodyPart = queryParams.bodyPart
		? bodyPartFilters.find(
				({ value }) =>
					queryParams.bodyPart.toLocaleLowerCase() ===
					value.toLocaleLowerCase()
		  )
		: null

	const clinicalStateFilter = Array.isArray(clinicalStates)
		? [
				{
					value: '',
					text: i18n`Any`,
				},
				...clinicalStates,
		  ]
		: []

	const selectedClinicalState = queryParams.clinicalProcessState
		? clinicalStateFilter.find(
				({ value }) =>
					queryParams.clinicalProcessState.toLocaleLowerCase() ===
					value.toLocaleLowerCase()
		  )
		: null

	const processInjuredStateFilter = Array.isArray(processInjuredStates)
		? [
				{
					value: '',
					text: i18n`Any`,
				},
				...processInjuredStates,
		  ]
		: []

	const selectedInjuredState = queryParams.processInjuredState
		? processInjuredStateFilter.find(
				({ value }) =>
					queryParams.processInjuredState.toLocaleLowerCase() ===
					value.toLocaleLowerCase()
		  )
		: null

	const availableNotificationTypeValues = useMemo(() => {
		if (!Array.isArray(notifications)) return null
		//console.log(notifications) Removed to next release
		// Try a few common field names to avoid coupling too hard to the API shape.
		const values = notifications
			.map((n) => {
				// backend DTO seems to send idNotificationType
				return (
					n?.idNotificationType ??
					n?.notificationTypeId ??
					n?.notificationType?.id ??
					n?.notificationType?.value
				)
			})
			.filter((v) => v !== undefined && v !== null && v !== '')
			.map((v) => String(v).toLowerCase())

		return new Set(values)
	}, [notifications])

	const notificationTypesFilteredByResults = useMemo(() => {
		if (!Array.isArray(notificationTypes)) return []
		if (!availableNotificationTypeValues) return notificationTypes

		return notificationTypes.filter(({ value }) =>
			availableNotificationTypeValues.has(String(value).toLowerCase())
		)
	}, [notificationTypes, availableNotificationTypeValues])

	const notificationTypesFilter = Array.isArray(
		notificationTypesFilteredByResults
	)
		? [
				{
					value: '',
					text: i18n`Any`,
				},
				...notificationTypesFilteredByResults,
		  ]
		: []

	const selectedNotificationType = queryParams.notificationType
		? notificationTypesFilter.find(
				({ value }) =>
					queryParams.notificationType.toLocaleLowerCase() ===
					String(value).toLocaleLowerCase()
		  )
		: null

	// If the selected value is no longer available (after filtering), default to "Any".
	const effectiveSelectedNotificationTypeValue =
		selectedNotificationType?.value ??
		notificationTypesFilter?.[0]?.value ??
		''

	const insuranceBranchesFilter = Array.isArray(insuranceBranches)
		? [
				{
					value: '',
					text: i18n`Any`,
				},
				...insuranceBranches,
		  ]
		: []

	const selectedInsuranceBranch = queryParams.branch
		? insuranceBranchesFilter.find(
				({ value }) =>
					queryParams.branch.toLocaleLowerCase() ===
					value.toLocaleLowerCase()
		  )
		: null

	return (
		<HStack wrap='wrap' gap='xsmall'>
			{insuranceCompaniesFilters.length ? (
				<Dropdown
					className={styles['options-entry']}
					placeholder={i18n`Ins. Company`}
					searchable
					value={
						selectedCompany
							? selectedCompany.value
							: insuranceCompaniesFilters[0].value
					}
					options={insuranceCompaniesFilters}
					onChange={(e) => {
						setQueryParams({
							company: e.target.value,
						})
					}}
				/>
			) : null}
			{providerFilters.length ? (
				<Dropdown
					className={styles['options-entry']}
					placeholder={i18n`Srv. Provider`}
					searchable
					value={
						selectedProvider
							? selectedProvider.value
							: providerFilters[0].value
					}
					options={providerFilters}
					onChange={(e) => {
						setQueryParams({
							providerEntity: e.target.value,
						})
					}}
				/>
			) : null}
			{bodyPartFilters.length ? (
				<Dropdown
					className={styles['options-entry']}
					placeholder={i18n`Body part`}
					searchable
					value={
						selectedBodyPart
							? selectedBodyPart.value
							: bodyPartFilters[0].value
					}
					options={bodyPartFilters}
					onChange={(e) => {
						setQueryParams({
							bodyPart: e.target.value,
						})
					}}
				/>
			) : null}
			{clinicalStateFilter.length ? (
				<Dropdown
					className={styles['options-entry']}
					placeholder={i18n`Clinical status`}
					searchable
					value={
						selectedClinicalState
							? selectedClinicalState.value
							: clinicalStateFilter[0].value
					}
					options={clinicalStateFilter}
					onChange={(e) => {
						setQueryParams({
							clinicalProcessState: e.target.value,
						})
					}}
				/>
			) : null}
			{processInjuredStateFilter.length ? (
				<Dropdown
					className={styles['options-entry']}
					placeholder={i18n`Injured status`}
					searchable
					value={
						selectedInjuredState
							? selectedInjuredState.value
							: processInjuredStateFilter[0].value
					}
					options={processInjuredStateFilter}
					onChange={(e) => {
						setQueryParams({
							processInjuredState: e.target.value,
						})
					}}
				/>
			) : null}
			{notificationTypesFilter.length ? (
				<Dropdown
					className={styles['options-entry']}
					placeholder={i18n`Notification Type`}
					searchable
					value={effectiveSelectedNotificationTypeValue}
					options={notificationTypesFilter}
					onChange={(e) => {
						setQueryParams({
							notificationType: e.target.value,
						})
					}}
				/>
			) : null}
			{insuranceBranchesFilter.length ? (
				<Dropdown
					className={styles['options-entry']}
					placeholder={i18n`Insurance branch`}
					searchable
					value={
						selectedInsuranceBranch
							? selectedInsuranceBranch.value
							: insuranceBranchesFilter[0].value
					}
					options={insuranceBranchesFilter}
					onChange={(e) => {
						setQueryParams({
							branch: e.target.value,
						})
					}}
				/>
			) : null}
		</HStack>
	)
}

export default NotificationsFilters
